import React, { useEffect, useMemo, useState } from 'react';
import Icon from './icons';
import CollectList from './CollectList';
import { changeOffice } from './office';
import CollectResult from './CollectResult';
import PortalPreview from './PortalPreview';
import SwapModal, { SwapState } from './SwapModal';
import { DragSrc } from './ItemList';
import { syncCollectFromReg } from './sync';
import { buildCollect, collectRows, cardDisplayName, rowStatus, getPerson, getCard, setLpName, newLP, tok, coItems } from './collect';
import { linkCompany } from './api';
import type { Collect, Project, Master } from './types';

interface Props {
  p: Project;
  onPatch: (patch: Partial<Project>) => void;
  toast: (m: string) => void;
}

export default function CollectPanel({ p, onPatch, toast }: Props) {
  const collect = useMemo<Collect>(() => p.collect ?? buildCollect(p), [p]);
  useEffect(() => {
    const base = p.collect ?? collect;
    const synced = syncCollectFromReg(p, base);
    if (!p.collect) onPatch({ collect: synced || collect });
    else if (synced) onPatch({ collect: synced });
  }, [p, collect, onPatch]);

  const [panel, setPanel] = useState<{ ref: string; view: 'list' | 'result' } | null>(null);
  const [editRow, setEditRow] = useState<string | null>(null);
  const [dragSrc, setDragSrc] = useState<DragSrc | null>(null);
  const [portalRef, setPortalRef] = useState<string | null>(null);
  const [swap, setSwap] = useState<SwapState | null>(null);

  const edit = (fn: (c: Collect) => void) => {
    const c = JSON.parse(JSON.stringify(collect)) as Collect;
    fn(c);
    onPatch({ collect: c });
  };
  // LP 삭제: collect에서 LP+소속법인 제거 + 등록정보(p.cos)에서도 동일 법인 제거
  // (p.cos를 그대로 두면 syncCollectFromReg가 다시 추가해 삭제가 되돌아감)
  const deleteLp = (ref: string) => {
    if (!ref.startsWith('lp:')) return;
    if (collect.lps.length <= 1) { toast('LP는 최소 1명이어야 합니다'); return; }
    const id = ref.slice(3);
    const c = JSON.parse(JSON.stringify(collect)) as Collect;
    const removed = c.cos.filter((co) => co.rep === ref);
    c.lps = c.lps.filter((l) => l.id !== id);
    c.cos = c.cos.filter((co) => co.rep !== ref);
    const patch: Partial<Project> = { collect: c };
    if (removed.length && p.cos && p.cos.length) {
      const keep = p.cos.filter((rc) => !removed.some((rm) => (!!rm.bizno && rm.bizno === rc.bizno) || (!!rm.name && rm.name === rc.name)));
      if (keep.length !== p.cos.length) patch.cos = keep;
    }
    if (panel && panel.ref === ref) setPanel(null);
    if (editRow === ref) setEditRow(null);
    onPatch(patch);
    toast('LP 삭제됨');
  };
  const togglePanel = (ref: string, view: 'list' | 'result') => {
    setPanel((cur) => (cur && cur.ref === ref && cur.view === view ? null : { ref, view }));
  };
  const toggleEditRow = (ref: string) => setEditRow((cur) => (cur === ref ? null : ref));

  const openPortal = (ref: string) => {
    const card = getPerson(collect, ref);
    if (!card.url) edit((c) => { const cc = getPerson(c, ref); if (!cc.url) cc.url = tok(); });
    setPortalRef(ref);
  };
  const openSwap = (ref: string, coId: string) => {
    const co = collect.cos.find((x) => x.id === coId);
    if (!co) { toast('연결된 투자법인이 없습니다'); return; }
    const person = getPerson(collect, ref);
    setSwap({
      ref, coId,
      name: co.name || '', bizno: co.bizno || '',
      rep: person.name || '', tel: ((person.recips && person.recips[0]) || { tel: '' }).tel || '',
      companyId: null,
    });
  };
  const applySwap = () => {
    if (!swap) return;
    const s = swap;
    if (!s.name.trim()) { toast('새 법인 명칭을 입력하세요'); return; }
    if (!s.rep.trim()) { toast('새 대표자 성명을 입력하세요'); return; }
    edit((c) => {
      const co = c.cos.find((x) => x.id === s.coId);
      if (!co) return;
      co.name = s.name; co.bizno = s.bizno;
      if (s.companyId) co.companyId = s.companyId;
      const person = getPerson(c, s.ref);
      person.name = s.rep;
      person.recips = [{ name: s.rep, role: s.ref === 'gp' ? 'GP 본인' : 'LP 본인', tel: s.tel, email: '' }];
      person.sent = false; person.results = null;
      co.items = coItems();
    });
    if (s.ref.startsWith('lp:') && p.master) {
      const idx = collect.lps.findIndex((l) => 'lp:' + l.id === s.ref);
      if (idx >= 0 && p.master.lps[idx]) {
        const m = JSON.parse(JSON.stringify(p.master)) as Master;
        m.lps[idx] = { name: s.rep, rrn: '', addr: '', amt: '', rel: '', tel: '' };
        onPatch({ master: m });
      }
    }
    setSwap(null);
    if (s.companyId) {
      linkCompany(p.id, s.companyId)
        .then(() => toast('투자법인·대표 교체 완료 · 업무관리 업체 연동됨'))
        .catch(() => toast('교체 완료 · 업체 연동 실패(네트워크 확인)'));
    } else {
      toast('투자법인·대표 LP 교체 완료 · 자료수집 일괄 갱신');
    }
  };

  const rows = collectRows(collect);
  const sentN = rows.filter((r) => r.card.sent).length;

  return (
    <>
      <div className="cgroup">
      <div className="cgroup-h">
        <span>조합원 {rows.length}명 (GP 1 · LP {collect.lps.length})</span>
        <span className="hs">{sentN}/{rows.length} 전송</span>
      </div>
      {rows.map((r, i) => {
        const open = panel && panel.ref === r.ref;
        const st = rowStatus(r.card);
        const mine = collect.cos.filter((co) => co.rep === r.ref);
        const role = r.type === 'gp' ? '업무집행조합원(GP)' : '유한책임조합원(LP)';
        const ed = editRow === r.ref;
        return (
          <React.Fragment key={r.ref}>
            <div className={'crow ' + (open ? 'open' : '')}>
              <span className="cnum">1-{i + 1}</span>
              <div className="cic"><Icon n="user" /></div>
              <div className="cmain cedit" onClick={() => toggleEditRow(r.ref)} title="클릭하여 정보 수정">
                <b>{cardDisplayName(collect, r.ref)}</b> <span className="crole">· {role}</span>
                {mine.length ? <span className="cmerge">🏢 대표법인 {mine.map((m) => m.name || '(법인명)').join(', ')}</span> : (r.type === 'lp' ? <span className="crole2">· 개인 LP</span> : null)}
                {' '}<span className="cpen">✎</span>
              </div>
              <span className={'pill ' + st.cls}>{st.label}</span>
              <button className={'lvbtn ' + (open && panel.view === 'list' ? 'on' : '')} onClick={() => togglePanel(r.ref, 'list')}>{open && panel.view === 'list' ? '✕ 닫기' : '리스트 보기'}</button>
              <button className={'lvbtn ' + (open && panel.view === 'result' ? 'on' : '')} onClick={() => togglePanel(r.ref, 'result')}>{open && panel.view === 'result' ? '✕ 닫기' : '결과 보기'}</button>
              {r.type === 'lp' ? <button className="lvbtn" onClick={() => deleteLp(r.ref)} title="이 LP 삭제">🗑 삭제</button> : null}
            </div>
            {ed ? (
              <div className="qedit">
                <div className="qf"><label>{r.type === 'gp' ? 'GP' : 'LP'} 성명</label>
                  <input value={r.card.name || ''} onChange={(e) => edit((c) => setLpName(c, r.ref, e.target.value))} placeholder="성명" /></div>
                {mine.map((co) => (
                  <React.Fragment key={co.id}>
                    <div className="qf"><label>대표법인 명칭</label><input value={co.name || ''} onChange={(e) => edit((c) => { getCard(c, 'co:' + co.id).name = e.target.value; })} placeholder="(주)○○" /></div>
                    <div className="qf"><label>법인 사업자등록번호</label><input value={co.bizno || ''} onChange={(e) => edit((c) => { getCard(c, 'co:' + co.id).bizno = e.target.value; })} placeholder="000-00-00000" /></div>
                  </React.Fragment>
                ))}
                <button className="qdone" onClick={() => toggleEditRow(r.ref)}>완료</button>
              </div>
            ) : null}
            {open ? (
              <div className="cpanel">
                {panel.view === 'list' ? (
                  <CollectList refId={r.ref} card={getPerson(collect, r.ref)} collect={collect} p={p} edit={edit} dragSrc={dragSrc} setDragSrc={setDragSrc} toast={toast} onAfterSend={(ref) => setPanel({ ref, view: 'result' })} onOpenPortal={openPortal} onOpenSwap={openSwap} onDeleteLp={deleteLp} onOffice={(o) => changeOffice(p, o, onPatch, toast)} />
                ) : (
                  <CollectResult refId={r.ref} card={getPerson(collect, r.ref)} p={p} edit={edit} toast={toast} />
                )}
              </div>
            ) : null}
          </React.Fragment>
        );
      })}
      <div className="caddlp"><button className="additem" onClick={() => edit((c) => { c.lps.push(newLP('', '')); })}><Icon n="plus" style={{ width: 13, height: 13 }} /> LP 추가</button></div>
      </div>
      {portalRef ? <PortalPreview p={p} collect={collect} refId={portalRef} onClose={() => setPortalRef(null)} /> : null}
      {swap ? <SwapModal swap={swap} onChange={(patch) => setSwap((s) => (s ? { ...s, ...patch } : s))} onApply={applySwap} onClose={() => setSwap(null)} /> : null}
    </>
  );
}
